export const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
export const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB

/**
 * Validates a single File/Blob object against max size limits.
 * @param {File|Blob} file 
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateMediaFile = (file) => {
	if (!file || !(file instanceof Blob || file instanceof File)) {
		return { valid: true };
	}

	const isVideo = file.type ? file.type.startsWith("video/") : /\.(mp4|mov|webm|ogg)$/i.test(file.name || "");
	if (isVideo) {
		if (file.size > MAX_VIDEO_SIZE) {
			const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
			return {
				valid: false,
				error: `Video size (${sizeMB}MB) exceeds the 50MB limit. Please choose a smaller video.`,
			};
		}
		return { valid: true };
	}

	const isImage = file.type ? file.type.startsWith("image/") : /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(file.name || "");
	if (isImage) {
		if (file.size > MAX_IMAGE_SIZE) {
			const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
			return {
				valid: false,
				error: `Image size (${sizeMB}MB) exceeds the 20MB limit. Please choose a smaller image.`,
			};
		}
		return { valid: true };
	}

	// General fallback limit
	if (file.size > MAX_VIDEO_SIZE) {
		return {
			valid: false,
			error: "File size exceeds the 50MB limit. Please choose a smaller file.",
		};
	}

	return { valid: true };
};

/**
 * Validates an array or collection of files.
 * @param {File[]|Blob[]} files 
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateMediaFiles = (files) => {
	if (!files || !Array.isArray(files)) return { valid: true };

	for (const file of files) {
		const result = validateMediaFile(file);
		if (!result.valid) return result;
	}

	return { valid: true };
};

/**
 * Parses axios/network error into a human-friendly error string,
 * especially handling HTTP 413 (Entity Too Large) and HTML responses.
 * @param {any} err 
 * @param {string} fallback 
 * @returns {string}
 */
export const getFriendlyUploadErrorMessage = (err, fallback = "Upload failed. Please try again.") => {
	if (!err) return fallback;

	// Check status 413
	if (err.response?.status === 413) {
		return "The uploaded file is too large for the server (Max 50MB). Please choose a smaller video.";
	}

	const responseData = err.response?.data;
	if (responseData) {
		if (typeof responseData === "string") {
			if (responseData.includes("413") || responseData.toLowerCase().includes("too large")) {
				return "The uploaded file is too large for the server (Max 50MB). Please choose a smaller video.";
			}
			// Avoid returning raw HTML error page
			if (responseData.trim().startsWith("<")) {
				return fallback;
			}
			return responseData;
		}
		if (responseData.error && typeof responseData.error === "string") {
			return responseData.error;
		}
		if (responseData.message && typeof responseData.message === "string") {
			return responseData.message;
		}
	}

	if (err.message && typeof err.message === "string" && !err.message.includes("413")) {
		return err.message;
	}

	return fallback;
};
